<template>
  <div class="clearfix rounded pb-5 mx-auto">
    <h4 class="atlhr-dashboard-title mt-10 px-0 mb-0">Service Book Settings<span>
        <a v-bind:href="api_url + '/company/dashboard'">Dashboard</a> / Settings / Service Book Settings</span>
    </h4>

    <b-overlay :show="loading || saving" rounded="sm" v-if="available">
      <div class="ibox mt-4">
        <div class="ibox-content">
          <form @submit.stop.prevent="submit">
            <div id="service_book_settings">
              <div class="clearfix row no-margin">
                <div class="col-md-12 fixed-section-one mb-4 px-0">
                  <div class="clearfix approval-container row bg-white">
                    <div class="col-4 approve-level-label">
                      <div class="approve-level-title">
                        Service Book eligibility
                      </div>
                      <div class="approve-level-description">
                        {{ side_copy }}
                      </div>
                    </div>

                    <div class="col-8 approve-level-content py-4">
                      <div class="clearfix px-0">
                        <div class="row mx-1 py-2 w-100 align-items-center no-margin px-4 border-bottom">
                          <div class="col-md-9 px-0 salary-heads">Restrict Service Book by employee type
                            <span class="sb-tip" tabindex="0" data-tip="When off, the Service Book is available to every employee. Turn this on to restrict access to specific employee types.">
                              <b-icon icon="info-circle"></b-icon>
                            </span>
                          </div>
                          <div class="col-md-3 text-right px-0">
                            <toggle-button v-model="eligibility_enabled" key="eligibility_enabled" :sync="true" :color="{ checked: '#004242', unchecked: '#e3342f', disabled: '#CCCCCC' }" @change="onEligibilityToggleChange" />
                          </div>
                        </div>

                        <template v-if="eligibility_enabled">
                          <div class="row mx-1 py-2 w-100 align-items-center no-margin px-4">
                            <div class="col-md-12 px-0 salary-heads font-weight-bold mb-2 mt-3">Eligible employee types</div>
                            <div class="col-md-12 px-0 text-muted small mb-4">Select which employee types are eligible for the Service Book</div>

                            <div class="col-md-12 px-0 mt-3">
                              <div class="row">
                                <div class="col-md-4 mb-3" v-for="type in employee_types" :key="type.id">
                                  <b-form-checkbox v-model="selected_ids" :value="type.id" class="border rounded p-2 m-1 shadow-sm">
                                    {{ type.name }}
                                  </b-form-checkbox>
                                </div>
                              </div>
                              <div v-if="!loading && employee_types.length === 0" class="text-muted mt-2">No employee types found.</div>
                            </div>
                          </div>
                        </template>
                        <div v-else class="row mx-1 py-3 w-100 no-margin px-4">
                          <div class="col-md-12 px-0 text-muted small">
                            All employees are eligible for the Service Book by default.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="btn-wrapper text-center pt-3" v-if="eligibility_enabled">
                <button type="submit" class="btn btn-primary btn-md px-5">Apply Changes</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
export default {
    props: {
        pageurl: {
            type: String,
            default: ''
        },
        companyid: {
            type: [Number, String],
            default: 0
        }
    },
    data() {
        return {
            employee_types: [],
            selected_ids: [],
            eligibility_enabled: false,
            // False when the optional Employee Type module is not installed. The whole
            // screen is a list of employee types, so with none to list there is nothing
            // here to configure — and eligibility falls back to "everyone", server-side.
            available: true,
            side_copy: "Set the eligibility criteria for the Service Book feature. By default, the feature is available to all employees. Enable the restriction to limit access to selected employee types.",
            loading: false,
            saving: false,
        };
    },
    computed: {
        api_url() {
            return this.pageurl || this.$api_url;
        },
    },
    created() {
        this.loadData();
    },
    methods: {
        // The employee types come back with the settings, from one request. They used to be
        // fetched separately from /employeetype/list, which is the optional Employee Type
        // module's own route — on a deployment without that module the route does not
        // exist, so the settings screen answered a missing optional module with a failed
        // request and an error toast. The Service Book endpoint answers either way, and
        // tells us whether the module is there at all.
        loadData() {
            this.loading = true;
            axios.get(this.api_url + '/admin-service-book/service-book-settings')
                .then(response => {
                    this.available = response.data.available !== false;
                    this.employee_types = response.data.employee_types || [];
                    this.selected_ids = (response.data.employee_type_ids || []).map(id => parseInt(id, 10));
                    this.eligibility_enabled = !!response.data.enabled;

                    // Tells Company Settings to drop the tab entirely rather than render an
                    // empty panel.
                    if (!this.available) {
                        this.$emit('unavailable');
                    }
                })
                .catch(() => {
                    this.$bvToast.toast('Failed to load Service Book settings.', { title: 'Error', variant: 'danger', solid: true });
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onEligibilityToggleChange() {
            this.submit();
        },
        submit() {
            this.saving = true;
            axios.post(this.api_url + '/admin-service-book/service-book-settings', {
                enabled: this.eligibility_enabled,
                employee_type_ids: this.selected_ids,
            })
                .then(response => {
                    this.selected_ids = (response.data.employee_type_ids || []).map(id => parseInt(id, 10));
                    this.eligibility_enabled = !!response.data.enabled;
                    this.$bvToast.toast('Service Book Settings saved successfully.', { title: 'Success', variant: 'success', solid: true });
                })
                .catch(() => {
                    this.$bvToast.toast('Failed to save Service Book settings.', { title: 'Error', variant: 'danger', solid: true });
                })
                .finally(() => {
                    this.saving = false;
                });
        },
    },
};
</script>

<style>
#service_book_settings .custom-control {
    padding-left: 6px !important;
}

#service_book_settings .custom-control-label::before,
#service_book_settings .custom-control-label::after {
    left: -28px !important;
}

/* Pure CSS tooltip */
#service_book_settings .sb-tip {
    position: relative;
    display: inline-flex;
    align-items: center;
    margin-left: 4px;
    color: #8a97a8;
    cursor: help;
    outline: none;
    transition: color .18s ease;
}

#service_book_settings .sb-tip:hover,
#service_book_settings .sb-tip:focus {
    color: #004242;
}

#service_book_settings .sb-tip::after,
#service_book_settings .sb-tip::before {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity .18s ease, transform .18s ease, visibility .18s;
}

/* bubble */
#service_book_settings .sb-tip::after {
    content: attr(data-tip);
    position: absolute;
    bottom: calc(100% + 10px);
    left: 50%;
    transform: translate(-50%, 6px);
    width: max-content;
    max-width: 260px;
    padding: 9px 12px;
    border-radius: 8px;
    background: #0f2f2f;
    background: linear-gradient(180deg, #0f3b3b 0%, #052a2a 100%);
    color: #fff;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.5;
    text-align: left;
    white-space: normal;
    box-shadow: 0 8px 20px rgba(0, 0, 0, .22);
    z-index: 1050;
}

/* arrow */
#service_book_settings .sb-tip::before {
    content: '';
    position: absolute;
    bottom: calc(100% + 4px);
    left: 50%;
    transform: translate(-50%, 6px);
    border: 6px solid transparent;
    border-top-color: #052a2a;
    z-index: 1051;
}

#service_book_settings .sb-tip:hover::after,
#service_book_settings .sb-tip:hover::before,
#service_book_settings .sb-tip:focus::after,
#service_book_settings .sb-tip:focus::before {
    opacity: 1;
    visibility: visible;
    transform: translate(-50%, 0);
}
</style>
